import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCloseThread, useStudioClients, useStudioProjects, useStudioTasks, useStudioTeam, useStudioThreads } from '@/api/studio';
import { NewDiscussionDialog } from '@/components/NewDiscussionDialog';
import { Thread } from '@/components/Thread';
import { Empty, PageHeader } from '@/components/ui';
import { QueryPending } from '@/components/QueryState';
import { WAIT_URGENT_HOURS, waitingLabel } from '@/lib/attention';
import { formatWhen } from '@/lib/format';

const SEARCH_MIN = 2;
const SEARCH_MAX = 100;

/** The text goes to the server after a short pause, so typing does not fire a request per letter. */
function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Every conversation with every client; «Только ждут нас» is the queue the team works from, the longest waiting on top. */
export function StudioThreads({ projectId, selectedId, base }: { projectId?: string; selectedId?: string; base: string }) {
  const { data: team } = useStudioTeam();
  const { data: clients } = useStudioClients();
  const { data: projects } = useStudioProjects();
  const close = useCloseThread();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const { data: tasks } = useStudioTasks(projectId ?? '');
  const taskKey = (id?: string) => tasks?.find((x) => x.id === id)?.key;
  const [onlyOurs, setOnlyOurs] = useState(!projectId);
  const [clientId, setClientId] = useState('');
  const [project, setProject] = useState('');
  const [text, setText] = useState('');
  const q = useDebounced(text.trim().length >= SEARCH_MIN ? text.trim() : '');

  const listQ = useStudioThreads({ q, clientId: projectId ? undefined : clientId || undefined, projectId: projectId ?? (project || undefined) });
  // The open conversation stays on screen even when a filter hides it from the list.
  const everyQ = useStudioThreads();
  const found = listQ.data;
  const selected = everyQ.data?.find((t) => t.id === selectedId);
  const waiting = (found ?? []).filter((t) => t.waitingOnUs);
  const list = onlyOurs ? waiting : (found ?? []);
  const projectChoices = (projects ?? []).filter((p) => !clientId || p.clientId === clientId);

  return (
    <div className="panel grid grid-cols-1 h-[calc(100vh-220px)] min-h-[520px] overflow-hidden lg:grid-cols-[360px_1fr]">
      <aside className="flex min-h-0 flex-col border-line lg:border-r">
        <div className="space-y-2 border-b border-line p-3">
          {projectId && (
            <button className="btn-primary w-full" onClick={() => setCreating(true)}>
              <Plus className="size-4" /> Новое обсуждение
            </button>
          )}
          <input className="field w-full" type="search" aria-label="Поиск по переписке" placeholder="Поиск по сообщениям" maxLength={SEARCH_MAX} value={text} onChange={(e) => setText(e.target.value)} />
          {!projectId && (
            <div className="grid grid-cols-2 gap-2">
              <select
                className="field"
                aria-label="Клиент"
                value={clientId}
                onChange={(e) => {
                  setClientId(e.target.value);
                  setProject('');
                }}
              >
                <option value="">Все клиенты</option>
                {(clients ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select className="field" aria-label="Проект" value={project} onChange={(e) => setProject(e.target.value)}>
                <option value="">Все проекты</option>
                {projectChoices.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex gap-1.5">
            <button className="chip h-6 px-2.5 text-xs" aria-pressed={onlyOurs} onClick={() => setOnlyOurs(true)}>
              Только ждут нас <span className="opacity-60">{waiting.length}</span>
            </button>
            <button className="chip h-6 px-2.5 text-xs" aria-pressed={!onlyOurs} onClick={() => setOnlyOurs(false)}>
              Все
            </button>
          </div>
        </div>
        <ul className="min-h-0 flex-1 divide-y divide-line overflow-y-auto">
          {!found && (
            <li className="p-4">
              <QueryPending query={listQ} compact />
            </li>
          )}
          {found && list.length === 0 && <li className="p-6 text-center text-sm text-muted">{onlyOurs ? 'Никто не ждёт ответа студии.' : 'Ничего не найдено.'}</li>}
          {list.map((t) => (
            <li key={t.id}>
              <Link to={`${base}/${t.id}`} className={clsx('block px-4 py-3', t.id === selectedId ? 'bg-sunken' : 'hover:bg-panel')}>
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{t.title}</span>
                  {t.taskId && taskKey(t.taskId) && <span className="num shrink-0 rounded-sm bg-sunken px-1.5 font-mono text-[11px] leading-4 text-muted">{taskKey(t.taskId)}</span>}
                  <time className="shrink-0 text-xs text-faint">{formatWhen(t.lastMessageAt)}</time>
                </div>
                {!projectId && <div className="text-xs text-muted">{t.client}</div>}
                <div className="mt-1 flex items-center gap-2">
                  <span className="line-clamp-1 flex-1 text-sm text-muted">{t.lastMessage}</span>
                  {t.waitingOnUs && t.waitingHours !== null && (
                    <span data-testid="waiting" className={clsx('num shrink-0 text-xs font-medium', t.waitingHours >= WAIT_URGENT_HOURS ? 'text-overdue' : 'text-waiting')}>
                      ждёт {waitingLabel(t.waitingHours)}
                    </span>
                  )}
                  {t.closed && <span className="shrink-0 text-xs text-faint">закрыт без ответа</span>}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </aside>
      <section className="flex min-h-0 flex-col">
        {selected && team ? (
          <>
            <div className="flex items-start gap-3 border-b border-line px-5 py-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium">{selected.title}</div>
                <div className="text-xs text-muted">
                  {selected.client}, {selected.total} сообщений. Вы пишете как {team[0].name}.
                </div>
              </div>
              {selected.waitingOnUs && (
                <button className="btn-secondary shrink-0" disabled={close.isPending} onClick={() => close.mutate(selected.id)}>
                  Закрыть без ответа
                </button>
              )}
            </div>
            <Thread key={selected.id} threadId={selected.id} as={team[0]} placeholder="Ответ клиенту" className="flex-1" />
          </>
        ) : (
          <div className="grid flex-1 place-items-center">
            <Empty title="Выберите переписку" />
          </div>
        )}
      </section>
      {projectId && <NewDiscussionDialog studio open={creating} onClose={() => setCreating(false)} projectId={projectId} tasks={tasks ?? []} onCreated={(t) => navigate(`${base}/${t.id}`)} />}
    </div>
  );
}

export function StudioInbox() {
  const { threadId } = useParams();
  return (
    <>
      <PageHeader title="Переписка" description="Все разговоры со всеми клиентами. Сначала те, где последнее слово за клиентом, самые давние выше. Дольше суток без ответа — красным." />
      <StudioThreads selectedId={threadId} base="/studio/inbox" />
    </>
  );
}
